/**
 * @copyright Copyright 2023-2024 Epic Systems Corporation
 * @file app screenshot with modal popup and carousel
 * @module Epic.AppOrchard.Showroom.ScreenshotViewer
 */

import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
	Button,
	Center,
	Image,
	Modal,
	ModalContent,
	ModalOverlay,
	Spacer,
	useDisclosure,
	Wrap,
	WrapItem,
} from "@chakra-ui/react";
import { showroomBorder } from "ao/chakraTheme";
import { IShowroomScreenshot } from "ao/types/showroom";
import { FunctionComponent, memo, useCallback, useState } from "react";

interface IProps {
	screenshots: IShowroomScreenshot[];
}

export const ScreenshotViewer: FunctionComponent<IProps> = memo((props) => {
	const { screenshots } = props;

	const { isOpen, onOpen, onClose } = useDisclosure();
	const [screenshotIndex, setScreenshotIndex] = useState<number>(0);

	const selectScreenshot = useCallback(
		(index: number) => () => {
			setScreenshotIndex(index);
			onOpen();
		},
		[onOpen],
	);

	return (
		<>
			{screenshots.length > 0 && (
				<Center>
					<Wrap spacing="30px" justify="center">
						{screenshots.map((x, idx) => (
							<WrapItem>
								<Button
									margin="0px"
									height="170px"
									width="300px"
									padding="0px"
									opacity={1}
									position="relative"
									borderRadius="10px"
									overflow="hidden"
									backgroundColor="white"
									border={showroomBorder}
									boxShadow="0px 3px 6px #00000029"
									_hover={{ boxShadow: "#afafaf 0px 0px 8px 2px", opacity: 0.7 }}
									onClick={selectScreenshot(idx)}
								>
									<Image
										maxWidth="100%"
										maxHeight="100%"
										src={"data:image/png;base64," + x.imageData}
										alt="App Screenshot"
									/>
								</Button>
							</WrapItem>
						))}
					</Wrap>
				</Center>
			)}
			<Modal isOpen={isOpen} onClose={onClose} isCentered preserveScrollBarGap>
				<ModalOverlay />

				<ModalContent maxWidth="70vw" maxHeight="70vh">
					<Center width="70vw" height="70vh">
						{screenshots.length > 1 && (
							<Button
								height="100%"
								variant="ghost"
								onClick={() =>
									setScreenshotIndex(
										screenshotIndex === 0 ? screenshots.length - 1 : screenshotIndex - 1,
									)
								}
								bgGradient="linear(to-r, gray.200, white)"
								_hover={{
									bgGradient: "linear(to-r, gray.300, white)",
								}}
							>
								<ChevronLeftIcon fontSize="50px" />
							</Button>
						)}

						<Spacer />
						<Image
							src={"data:image/png;base64," + screenshots[screenshotIndex]?.imageData}
							maxWidth="60vw"
							maxHeight="70vh"
							backgroundColor="white"
						/>
						<Spacer />
						{screenshots.length > 1 && (
							<Button
								height="100%"
								variant="ghost"
								onClick={() => setScreenshotIndex((screenshotIndex + 1) % screenshots.length)}
								bgGradient="linear(to-r, white, gray.200)"
								_hover={{
									bgGradient: "linear(to-r, white, gray.300)",
								}}
							>
								<ChevronRightIcon fontSize="50px" />
							</Button>
						)}
					</Center>
				</ModalContent>
			</Modal>
		</>
	);
});
